import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useParams } from "@tanstack/react-router";
import { CUSTOMER_WORKSHEET_HEADER, type CustomerWorksheetView } from "shared";
import { FOOTER_TEXT } from "../components/Footer";

const NOT_AVAILABLE = "This link is not available";
const TOO_MANY = "Too many attempts. Try again later.";
const HINT = "If you typed the digits correctly and still see this message, ask your helper for a new link.";
const TITLE = "Your preparation worksheet";
type CheckKind = "zip3" | "phone4";

/** Plain fetch: no cookies, no CSRF, no shared API wrapper (which would touch auth state). The token only travels in the body. */
async function post(path: string, body: unknown): Promise<{ status: number; data: unknown }> {
  const res = await fetch(path, {
    method: "POST", credentials: "omit", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  let data: unknown = null;
  try { data = await res.json(); } catch { /* uniform handling by status only */ }
  return { status: res.status, data };
}
/** Only plain https URLs without embedded credentials may become links; anything else is shown as text. */
function safeHttps(value: string | null | undefined): string | null {
  if (!value || !value.startsWith("https://")) return null;
  try {
    const u = new URL(value);
    return u.protocol === "https:" && !u.username && !u.password ? value : null;
  } catch { return null; }
}
const messageFor = (status: number) => (status === 429 ? TOO_MANY : NOT_AVAILABLE);

/** A tel: link only for a value that is one plain phone number; anything else (extensions, two numbers) stays text. */
function telHref(value: string): string | undefined {
  const v = value.trim();
  const digits = v.replace(/\D/g, "");
  if (!/^\+?[\d\s().-]+$/.test(v) || digits.length < 7 || digits.length > 11) return undefined;
  return `tel:${v.startsWith("+") ? "+" : ""}${digits}`;
}

/**
 * Public, login-free page. The token, the check digits and the view live only in component state:
 * nothing is written to storage, cookies, the console or the document title, and a reload starts over at step 1.
 * Print: the header sits in a table <thead>, which Chromium repeats at the top of every printed page.
 */
export function CustomerShare() {
  const { token } = useParams({ strict: false }) as { token: string };
  const [kind, setKind] = useState<CheckKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState(false);
  const [check, setCheck] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<CustomerWorksheetView | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { document.title = TITLE; }, []);
  useEffect(() => {
    let live = true;
    post("/api/public/share/kind", { token }).then((r) => {
      if (!live) return;
      const k = (r.data as { checkKind?: string } | null)?.checkKind;
      if (r.status === 200 && (k === "zip3" || k === "phone4")) setKind(k);
      else setError(messageFor(r.status));
    }).catch(() => live && setError(NOT_AVAILABLE));
    return () => { live = false; };
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setHint(false);
    try {
      const r = await post("/api/public/share/open", { token, check });
      const v = (r.data as { view?: CustomerWorksheetView } | null)?.view;
      if (r.status === 200 && v) { setView(v); setCheck(""); }
      else { setError(messageFor(r.status)); setHint(r.status !== 429); }
    } catch {
      setError(NOT_AVAILABLE); setHint(true);
    } finally { setBusy(false); }
    // On failure the form is still mounted: put the cursor back on the digits, selected, ready to retype.
    inputRef.current?.focus();
    inputRef.current?.select();
  }

  const digits = kind === "zip3" ? 3 : 4;
  const prompt = kind === "zip3" ? "Enter the last 3 digits of your ZIP code" : "Enter the last 4 digits of your phone number";

  return (
    <Frame header={view?.header ?? CUSTOMER_WORKSHEET_HEADER}>
      <div aria-live="polite" role="status" className="min-h-[1px]">
        {error && (
          <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-base text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100">
            <p className="font-semibold">{error}</p>
            {hint && <p className="mt-1">{HINT}</p>}
          </div>
        )}
      </div>
      {view ? <Worksheet view={view} /> : kind && (
        <form onSubmit={submit} className="no-print card space-y-3">
          <label htmlFor="share-check" className="block text-base font-medium">{prompt}</label>
          <input id="share-check" className="input share-input" type="text" inputMode="numeric" pattern="[0-9]*"
            autoComplete="off" ref={inputRef} value={check} onChange={(e) => setCheck(e.target.value.replace(/\D/g, "").slice(0, digits))} />
          <button type="submit" className="btn-primary share-btn" disabled={busy || check.length !== digits}>Continue</button>
        </form>
      )}
    </Frame>
  );
}

function Frame({ header, children }: { header: string; children: ReactNode }) {
  return (
    <div className="share-page min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <table role="presentation" className="share-frame mx-auto w-full max-w-2xl">
        <thead role="presentation"><tr role="presentation"><th role="presentation" className="px-4 pt-4 text-left font-normal">
          <p className="share-header rounded-md border border-slate-300 bg-white p-3 text-base font-semibold dark:border-slate-700 dark:bg-slate-900">{header}</p>
        </th></tr></thead>
        <tbody role="presentation"><tr role="presentation"><td role="presentation" className="px-4 py-4 align-top">
          <main>{children}</main>
          <footer className="mt-8 border-t border-slate-200 pt-3 text-sm text-slate-600 dark:border-slate-800 dark:text-slate-400">{FOOTER_TEXT}</footer>
        </td></tr></tbody>
      </table>
    </div>
  );
}

function Worksheet({ view }: { view: CustomerWorksheetView }) {
  const { official } = view;
  const apply = safeHttps(official.applyUrl) ?? safeHttps(official.officialUrl);
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => { headingRef.current?.focus(); }, []);
  const expires = new Date(view.expiresAt);
  return (
    <div className="space-y-6">
      <section>
        <h1 ref={headingRef} tabIndex={-1} className="text-2xl font-semibold focus:outline-none">{view.title}</h1>
        <p className="mt-2 whitespace-pre-line text-base">{view.instructions}</p>
      </section>

      <section aria-labelledby="answers-h">
        <h2 id="answers-h" className="mb-2 text-lg font-semibold">Your answers</h2>
        <dl className="space-y-4">
          {view.fields.map((f, i) => (
            <div key={i} className="card">
              <dt className="text-sm font-semibold text-slate-700 dark:text-slate-300">{f.label}</dt>
              <dd className="mt-1 text-base">
                {f.sensitive ? <em>Enter this on the official site</em> : (f.value || <span className="text-slate-500">Not provided</span>)}
                {f.help && <span className="block text-sm text-slate-600 dark:text-slate-400">{f.help}</span>}
                {f.officialFieldRef && <small className="block text-xs text-slate-600 dark:text-slate-400">Official form: {f.officialFieldRef}</small>}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {view.documents.length > 0 && (
        <section aria-labelledby="docs-h">
          <h2 id="docs-h" className="mb-2 text-lg font-semibold">Documents to have ready</h2>
          <ul className="space-y-2">
            {view.documents.map((d, i) => (
              <li key={i}>
                <label className="flex items-start gap-3 text-base">
                  <input type="checkbox" className="mt-1 h-5 w-5" />
                  <span>{d.item}{d.note && <span className="block text-sm text-slate-600 dark:text-slate-400">{d.note}</span>}</span>
                </label>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-labelledby="apply-h">
        <h2 id="apply-h" className="mb-2 text-lg font-semibold">{official.programName}</h2>
        {apply && (
          <a href={apply} target="_blank" rel="noopener noreferrer" className="btn-primary share-btn">Apply on the official website</a>
        )}
        {official.contacts.length > 0 && (
          <ul className="mt-4 space-y-2 text-base">
            {official.contacts.map((c, i) => (
              <li key={i}>
                {c.label && <span className="font-medium">{c.label}: </span>}
                {c.kind === "phone" && telHref(c.value) ? <a className="underline" href={telHref(c.value)}>{c.value}</a>
                  : c.kind === "web" && safeHttps(c.value) ? <a className="underline" href={c.value} target="_blank" rel="noopener noreferrer">{c.value}</a>
                  : <span>{c.value}</span>}
                {c.hours && <span className="text-sm text-slate-600 dark:text-slate-400"> ({c.hours})</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="no-print">
        <button type="button" className="btn-ghost share-btn border border-slate-300 dark:border-slate-700" onClick={() => window.print()}>Print / Save as PDF</button>
      </div>
      {!Number.isNaN(expires.getTime()) && (
        <p className="text-sm text-slate-600 dark:text-slate-400">This link expires on {expires.toLocaleDateString(undefined, { dateStyle: "long" })}.</p>
      )}
    </div>
  );
}
